import { BarChartOutlined, DownOutlined, RightOutlined } from '@ant-design/icons';

import { useT } from '../../../../i18n';
import { cn } from '../../../../lib/cn';
import { NHAN_TRANG_THAI, TONG_TRANG_THAI } from '../../constants';
import { phanTram } from '../../nghiep-vu/nhiem-vu';
import type { LocTrangThai, ThongKeNhiemVu } from '../../types-nhiem-vu';

interface Props {
  dashboard: ThongKeNhiemVu | undefined;
  open: boolean;
  onToggle: () => void;
}

/** Bốn nhóm không chồng nhau nên cộng lại đúng bằng tổng — vẽ được thành một thanh. */
const DOAN = [
  { key: 'moi_phan_cong', status: 'MOI_PHAN_CONG' },
  { key: 'dang_thuc_hien', status: 'DANG_THUC_HIEN' },
  { key: 'qua_han', status: 'QUA_HAN' },
  { key: 'hoan_thanh', status: 'HOAN_THANH' },
] as const satisfies readonly { key: keyof ThongKeNhiemVu; status: LocTrangThai }[];

export function TaskStatsPanel({ dashboard: d, open, onToggle }: Props) {
  const { t } = useT();
  return (
    <section className="rounded-lg border border-default bg-surface shadow-sm">
      <button
        type="button"
        aria-expanded={open}
        aria-controls="nv-thong-ke"
        onClick={onToggle}
        className="flex w-full items-center gap-3 px-4 py-3 text-left"
      >
        <BarChartOutlined aria-hidden className="text-primary-600" />
        <span className="min-w-0 flex-1">
          <span className="block text-sm font-semibold text-content-primary">{t('vanBanNhiemVu.thongKe.nut')}</span>
          <span className="block text-xs text-content-muted">{t('vanBanNhiemVu.thongKe.nutMo')}</span>
        </span>
        {open ? <DownOutlined aria-hidden className="text-xs" /> : <RightOutlined aria-hidden className="text-xs" />}
      </button>
      {open && (
        <div id="nv-thong-ke" className="grid grid-cols-1 gap-6 border-t border-default p-4 lg:grid-cols-5">
          <StatusBreakdown dashboard={d} />
          <OrgBreakdown dashboard={d} />
        </div>
      )}
    </section>
  );
}

function StatusBreakdown({ dashboard: d }: { dashboard: ThongKeNhiemVu | undefined }) {
  const { t } = useT();
  const total = d?.total ?? 0;
  return (
    <div className="lg:col-span-2">
      <h3 className="mb-3 text-xs font-semibold uppercase tracking-wide text-content-secondary">
        {t('vanBanNhiemVu.thongKe.tyLe')}
      </h3>
      <div role="img" aria-label={t('vanBanNhiemVu.thongKe.tyLeAria')} className="flex h-3 overflow-hidden rounded-full bg-subtle">
        {d &&
          DOAN.map((s) =>
            d[s.key] > 0 ? (
              <div key={s.key} className={TONG_TRANG_THAI[s.status].dot} style={{ width: `${(d[s.key] / total) * 100}%` }} />
            ) : null,
          )}
      </div>
      <ul className="mt-3 space-y-2">
        {DOAN.map((s) => (
          <li key={s.key} className="flex items-center gap-2 text-xs">
            <span aria-hidden className={cn('h-2.5 w-2.5 shrink-0 rounded-full', TONG_TRANG_THAI[s.status].dot)} />
            <span className="flex-1 text-content-secondary">{t(NHAN_TRANG_THAI[s.status])}</span>
            <span className="font-semibold tabular-nums text-content-primary">{d?.[s.key] ?? 0}</span>
            <span className="w-10 text-right tabular-nums text-content-muted">{phanTram(d?.[s.key] ?? 0, total)}%</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function OrgBreakdown({ dashboard: d }: { dashboard: ThongKeNhiemVu | undefined }) {
  const { t } = useT();
  const rows = d?.by_org ?? [];
  return (
    <div className="lg:col-span-3">
      <h3 className="mb-3 text-xs font-semibold uppercase tracking-wide text-content-secondary">
        {t('vanBanNhiemVu.thongKe.theoDonVi')}
      </h3>
      {rows.length === 0 ? (
        <p className="py-6 text-center text-xs text-content-muted">{t('vanBanNhiemVu.thongKe.theoDonViRong')}</p>
      ) : (
        <ul className="max-h-48 space-y-2.5 overflow-y-auto pr-1">
          {rows.map((r) => (
            <li key={r.org_name} className="grid grid-cols-[minmax(0,11rem)_1fr_2rem] items-center gap-3 text-xs">
              <span className="truncate font-medium text-content-primary" title={r.org_name}>
                {r.org_name}
              </span>
              <div className="flex h-2.5 overflow-hidden rounded-full bg-subtle">
                {DOAN.map((s) =>
                  r[s.key] > 0 ? (
                    <div
                      key={s.key}
                      className={TONG_TRANG_THAI[s.status].dot}
                      style={{ width: `${(r[s.key] / r.total) * 100}%` }}
                      title={`${t(NHAN_TRANG_THAI[s.status])}: ${r[s.key]}`}
                    />
                  ) : null,
                )}
              </div>
              <span className="text-right font-semibold tabular-nums text-content-primary">{r.total}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
